import classNames from 'classnames';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';

import ClientsIcon from '../assets/icons/clients.svg?react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, generatePath } from 'react-router-dom';

import { PeopleApi } from '../api';
import { PrivateRoute } from '../components';
import { RoutePath } from '../config';
import { DoctorInnerPageLayout } from '../layouts';

const DEBOUNCE_MS = 300;

export const DoctorPatientsPage = () => {
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(30);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const { data: patientsData } = useQuery({
    queryFn: () =>
      PeopleApi.getPatients(debouncedSearch, currentPage, pageSize),
    queryKey: [
      PeopleApi.KEY,
      'getPatients',
      debouncedSearch,
      currentPage,
      pageSize,
    ],
    placeholderData: (previousData) => previousData,
  });

  const patients = patientsData?.items || [];
  const totalPatients = patientsData?.total || 0;
  const totalPages = Math.ceil(totalPatients / pageSize);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return (
    <PrivateRoute redirectPath={RoutePath.DoctorLogin}>
      <DoctorInnerPageLayout>
        <div className="size-full overflow-hidden rounded-[16px] bg-brand-6">
          <header className="flex h-[106px] gap-x-[24px] p-[24px]">
            <div className="relative max-w-md flex-1">
              <input
                className={classNames(
                  'input transition-none w-full pr-[90px]',
                  {
                    'input--search': !searchText,
                  },
                )}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Введите ФИО клиента"
                type="text"
                value={searchText}
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => setSearchText('')}
                  className="absolute right-[6px] top-1/2 flex size-[40px] -translate-y-1/2 items-center justify-center rounded-full text-brand-1/60 hover:bg-brand-2 hover:text-brand-1"
                  aria-label="Очистить"
                >
                  <svg
                    className="size-[20px]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              )}
            </div>
            <Link
              className="button button--primary px-[24px]"
              to={RoutePath.DoctorCreatePatient}
            >
              Добавить&nbsp;клиента
            </Link>
          </header>
          <div className="flex size-full max-h-[calc(100%_-_106px)] flex-col rounded-[16px] bg-white  p-[24px]">
            {patients.length === 0 ? (
              <div className="flex size-full flex-col items-start justify-center gap-[10px] self-center rounded-[12px] px-[16px] py-[24px] outline outline-[.9] outline-offset-[-1px] outline-[#151515]/10">
                <div className="flex w-full flex-col items-center justify-center gap-[32px]">
                  <div className="inline-flex items-center justify-start gap-[10px] rounded-[100px] bg-[#f1f7fa] p-[12px]">
                    <ClientsIcon className="size-[24px] text-[#0788f1]" />
                  </div>
                  <div className="flex w-full flex-col items-start gap-[8px]">
                    <div className="w-full text-center font-['Ubuntu'] text-[20px] font-medium leading-[28px] text-[#151515]">
                      У вас пока нет ни одного клиента
                    </div>
                    <div className="w-full text-center font-['Ubuntu'] text-[14px] font-normal leading-[20px] text-[#151515] opacity-50">
                      Вы можете добавить клиента вручную, заполнив его данные.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="min-h-0 flex-1 overflow-auto p-[24px]">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="w-[24px]" />
                        <th className="pl-[24px] text-left">Клиент</th>
                        <th className="text-left">Контакты</th>
                        <th className="text-left">Текущий заказ</th>
                        <th className="text-left">Заказы</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients.map((patient) => {
                        const pathToPatientDetails = generatePath(
                          RoutePath.DoctorPatientDetails,
                          {
                            patientId: patient.id.toString(),
                          },
                        );

                        return (
                          <tr className="leading-[24px]" key={patient.id}>
                            <td>
                              <div className="flex size-[60px] items-center justify-center rounded-[12px] bg-brand-7 text-[22px]">
                                {patient.initials}
                              </div>
                            </td>
                            <td className="pl-[24px]">
                              <Link
                                className="block underline hover:no-underline"
                                to={pathToPatientDetails}
                              >
                                {patient.fullName}
                              </Link>
                              {patient.birthdate &&
                                isValid(patient.birthdate) && (
                                  <span className="text-brand-1/50">
                                    {format(patient.birthdate, 'dd.MM.yyyy')}
                                  </span>
                                )}
                            </td>
                            <td>
                              {patient.phone}
                              {patient.email && (
                                <span className="block text-[14px] underline">
                                  {patient.email}
                                </span>
                              )}
                            </td>
                            <td className="underline">
                              №{patient.active_order?.id}
                            </td>
                            <td className="text-brand-5 underline hover:no-underline">
                              <Link to={pathToPatientDetails}>
                                История заказов
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex shrink-0 items-center justify-between border-t border-[#151515]/10 px-[24px] py-[16px]">
                  <div>
                    <span>Страница {currentPage}</span>
                  </div>
                  <div className="flex items-center gap-x-[8px]">
                    <button
                      className="button--secondary button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Назад
                    </button>
                    <span className="text-[#151515]/40">|</span>
                    <button
                      className="button--secondary button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Вперед
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DoctorInnerPageLayout>
    </PrivateRoute>
  );
};
